import React, { useEffect, useState } from 'react'
import { Modal, Form, Input, Checkbox, Space, Button, Spin, Empty, Divider } from 'antd'
import { CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import type { Role, PermissionGroup } from '../types/role.types'
import roleService from '../services/role.service'

interface RoleFormModalProps {
    open: boolean
    onCancel: () => void
    onSubmit: (values: any) => void
    initialValues?: Role | null
    loading?: boolean
}

export const RoleFormModal: React.FC<RoleFormModalProps> = ({
    open,
    onCancel,
    onSubmit,
    initialValues,
    loading = false
}) => {
    const [form] = Form.useForm()
    const [searchText, setSearchText] = useState('')
    const [selectedPermissions, setSelectedPermissions] = useState<number[]>([])

    // Fetch permissions from API
    const { data: permissionGroups = [], isLoading: isLoadingPermissions } = useQuery({
        queryKey: ['permissions'],
        queryFn: roleService.getPermissions,
        enabled: open, // Only fetch when modal is open
    })

    useEffect(() => {
        if (open) {
            if (initialValues) {
                const permissions = initialValues.permissions || []
                setSelectedPermissions(permissions)
                form.setFieldsValue({
                    name: initialValues.name,
                    display_name: initialValues.display_name,
                    description: initialValues.description,
                })
            } else {
                setSelectedPermissions([])
                form.resetFields()
            }
        }
    }, [open, initialValues, form])

    const handleOk = () => {
        form.validateFields().then(values => onSubmit({ ...values, permissions: selectedPermissions }))
    }

    // Filter permission groups based on search
    const filteredGroups = permissionGroups
        .map(group => ({
            ...group,
            permissions: group.permissions.filter(perm =>
                perm.display_name.toLowerCase().includes(searchText.toLowerCase()) ||
                perm.name.toLowerCase().includes(searchText.toLowerCase()) ||
                (perm.description?.toLowerCase().includes(searchText.toLowerCase()))
            )
        }))
        .filter(group => group.permissions.length > 0)

    // Handle select all for a category
    const handleSelectAll = (group: PermissionGroup, checked: boolean) => {
        const groupPermissions = group.permissions.map(p => p.id)
        let newPermissions: number[]

        if (checked) {
            // Add all permissions from this group
            newPermissions = [...new Set([...selectedPermissions, ...groupPermissions])]
        } else {
            // Remove all permissions from this group
            newPermissions = selectedPermissions.filter(p => !groupPermissions.includes(p))
        }

        setSelectedPermissions(newPermissions)
    }

    // Check if all permissions in a group are selected
    const isGroupFullySelected = (group: PermissionGroup) => {
        return group.permissions.every(p => selectedPermissions.includes(p.id))
    }

    // Check if some permissions in a group are selected
    const isGroupPartiallySelected = (group: PermissionGroup) => {
        const selected = group.permissions.filter(p => selectedPermissions.includes(p.id))
        return selected.length > 0 && selected.length < group.permissions.length
    }

    const handlePermissionChange = (checkedValues: number[]) => {
        setSelectedPermissions(checkedValues)
    }

    return (
        <Modal
            title={initialValues ? "Edit Role" : "Create New Role"}
            open={open}
            onOk={handleOk}
            onCancel={onCancel}
            width={800}
            confirmLoading={loading}
            styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    label="Role Name"
                    name="name"
                    rules={[{ required: true, message: 'Please enter role name' }]}
                >
                    <Input placeholder="e.g., support_agent" />
                </Form.Item>
                <Form.Item
                    label="Display Name"
                    name="display_name"
                    rules={[{ required: true, message: 'Please enter display name' }]}
                >
                    <Input placeholder="e.g., Support Agent" />
                </Form.Item>
                <Form.Item
                    label="Description"
                    name="description"
                    rules={[{ required: true, message: 'Please enter description' }]}
                >
                    <Input.TextArea rows={2} placeholder="Brief description of this role..." />
                </Form.Item>

                <Divider />

                <Form.Item
                    label={
                        <Space>
                            <span>Permissions</span>
                            <span style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 'normal' }}>
                                ({selectedPermissions.length} selected)
                            </span>
                        </Space>
                    }
                >
                    <Spin spinning={isLoadingPermissions}>
                        <Input.Search
                            placeholder="Search permissions..."
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ marginBottom: 16 }}
                            allowClear
                        />

                        {filteredGroups.length === 0 ? (
                            <Empty description="No permissions found" />
                        ) : (
                            <Checkbox.Group
                                style={{ width: '100%' }}
                                value={selectedPermissions}
                                onChange={handlePermissionChange}
                            >
                                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                    {filteredGroups.map((group) => {
                                        const isFullySelected = isGroupFullySelected(group)
                                        const isPartiallySelected = isGroupPartiallySelected(group)

                                        return (
                                            <div
                                                key={group.category}
                                                style={{
                                                    border: '1px solid #d9d9d9',
                                                    borderRadius: 8,
                                                    padding: 16,
                                                    background: isFullySelected ? '#e6f7ff' : isPartiallySelected ? '#fff7e6' : 'white',
                                                    borderColor: isFullySelected ? '#91d5ff' : isPartiallySelected ? '#ffd591' : '#d9d9d9'
                                                }}
                                            >
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    marginBottom: 12
                                                }}>
                                                    <Space>
                                                        <strong style={{ fontSize: 15 }}>{group.category}</strong>
                                                        <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                                                            ({group.permissions.filter(p => selectedPermissions.includes(p.id)).length}/{group.permissions.length})
                                                        </span>
                                                    </Space>
                                                    <Button
                                                        size="small"
                                                        type={isFullySelected ? "default" : "primary"}
                                                        icon={isFullySelected ? <CloseOutlined /> : <CheckOutlined />}
                                                        onClick={() => handleSelectAll(group, !isFullySelected)}
                                                    >
                                                        {isFullySelected ? 'Deselect All' : 'Select All'}
                                                    </Button>
                                                </div>

                                                <div style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                                    gap: '12px',
                                                    rowGap: '8px'
                                                }}>
                                                    {group.permissions.map((perm) => (
                                                        <Checkbox
                                                            key={perm.id}
                                                            value={perm.id}
                                                            style={{ margin: 0 }}
                                                        >
                                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                                <span style={{ fontWeight: 500 }}>{perm.display_name}</span>
                                                                {perm.description && (
                                                                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                                                                        {perm.description}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </Checkbox>
                                                    ))}
                                                </div>
                                            </div>
                                        )
                                    })}
                                </Space>
                            </Checkbox.Group>
                        )}
                    </Spin>
                </Form.Item>
            </Form>
        </Modal>
    )
}
